'use client';

import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  clearPendingInactivityToast,
  getPendingInactivityToastStorage,
  readPendingInactivityToastMinutes,
} from '@core/utils/inactivity-sign-out';
import NotificationBanner from '@/components/NotificationBanner';
import { useSession } from '@/contexts/session.context';
import { useLocalStorage } from '@uidotdev/usehooks';

type NoticeTone = 'success' | 'error' | 'default' | 'warning';

interface StoredNotification {
  id: string;
  message: string;
  variant: 'success' | 'error' | 'default';
  timestamp: number;
}

interface PageNotice {
  id: string;
  title: string;
  description?: string;
  tone: NoticeTone;
}

const TONE_CONFIG: Record<
  NoticeTone,
  {
    variant: 'success' | 'destructive' | 'warning' | 'info';
    icon: 'success' | 'alert' | 'warning' | 'info';
  }
> = {
  success: { variant: 'success', icon: 'success' },
  error: { variant: 'destructive', icon: 'alert' },
  warning: { variant: 'warning', icon: 'warning' },
  default: { variant: 'info', icon: 'info' },
};

export default function PageNotices() {
  const { formatMessage } = useIntl();
  const { status } = useSession();
  const [notices, setNotices] = useState<PageNotice[]>([]);
  const [, setStored] = useLocalStorage<StoredNotification[]>(
    'scribeberry_notifications',
    [],
  );

  useEffect(() => {
    const publishStoredNotices = () => {
      let items: StoredNotification[] = [];
      try {
        const raw = window.localStorage.getItem('scribeberry_notifications');
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed) || parsed.length === 0) return;
        items = parsed;
      } catch {
        return;
      }
      setNotices((prev) => {
        const seen = new Set(prev.map((notice) => notice.id));
        const incoming: PageNotice[] = items
          .filter((item) => !seen.has(item.id))
          .map((item) => ({
            id: item.id,
            title: item.message,
            tone: item.variant,
          }));
        return incoming.length > 0 ? [...prev, ...incoming] : prev;
      });
      setStored([]);
    };

    publishStoredNotices();
    window.addEventListener('storage', publishStoredNotices);
    return () => window.removeEventListener('storage', publishStoredNotices);
  }, [setStored]);

  useEffect(() => {
    if (status !== 'unauthenticated') return;
    const storage = getPendingInactivityToastStorage();
    const minutes = readPendingInactivityToastMinutes(storage);
    if (minutes === null) return;
    clearPendingInactivityToast(storage);
    setNotices((prev) => [
      ...prev,
      {
        id: `inactivity-${Date.now()}`,
        title: formatMessage({ id: 'signed_out_due_to_inactivity' }),
        description: formatMessage(
          { id: 'signed_out_due_to_inactivity_description' },
          { minutes },
        ),
        tone: 'warning',
      },
    ]);
  }, [status, formatMessage]);

  const dismiss = (id: string) =>
    setNotices((prev) => prev.filter((notice) => notice.id !== id));

  if (notices.length === 0) return null;

  return (
    <div className='pointer-events-none fixed inset-x-0 top-0 z-[100] flex flex-col items-center gap-2 p-4'>
      {notices.map((notice) => {
        const config = TONE_CONFIG[notice.tone];
        return (
          <div
            key={notice.id}
            className='pointer-events-auto w-full max-w-md'
          >
            <NotificationBanner
              title={notice.title}
              description={notice.description}
              variant={config.variant}
              icon={config.icon}
              onDismiss={() => dismiss(notice.id)}
            />
          </div>
        );
      })}
    </div>
  );
}
